import moment from 'moment';
import { DateTime } from 'luxon';
import getFiscalEntitiesFromInterval from './contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider/getFiscalEntitiesFromInterval';
import getCurrentFiscalEntity from './contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider/getCurrentFiscalEntity';
import relativeDateRangeTypeCheckers from './contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider/relativeDateRangeTypeCheckers';

export const getEndOf = (
  date: number,
  interval?: FleetOps.Interval | FiscalInterval,
  fiscalRanges?: FixedPeriods.AllFiscals,
) => {
  if (!interval) {
    return date;
  }

  if (relativeDateRangeTypeCheckers.isFiscalInterval(interval)) {
    const fiscalEntities = getFiscalEntitiesFromInterval(
      interval,
      fiscalRanges,
    );
    if (!fiscalEntities) {
      const e = new Error('No fiscal entities found');
      e.name = 'RemovePartialEntities: No fiscal entities found';
      throw e;
    }
    const current = getCurrentFiscalEntity(
      fiscalEntities,
      DateTime.fromMillis(date),
    );
    if (current) {
      return DateTime.fromISO(current.endDate).plus({ day: 1 }).toMillis();
    }
    return date;
  }

  switch (interval) {
    case 'month':
      return DateTime.fromMillis(date).plus({ month: 1 }).toMillis();
    case 'week':
      return DateTime.fromMillis(date).plus({ week: 1 }).toMillis();
    case 'year':
      return DateTime.fromMillis(date).plus({ year: 1 }).toMillis();
    case 'quarter':
      return DateTime.fromMillis(date).plus({ quarter: 1 }).toMillis();
    default:
    case 'auto':
    case 'day':
    case 'hour':
      return date;
  }
};

export const getIsLastDatePartial = ({
  date,
  interval,
  dateScope,
  fiscalRanges,
}: {
  date: string | number;
  interval?: FleetOps.Interval | FiscalInterval;
  dateScope?: DateRangeInput;
  fiscalRanges?: FixedPeriods.AllFiscals;
}) => {
  const dateToCompareTo = (() => {
    if (!dateScope || !dateScope.endDate) {
      const today = moment.utc().startOf('day');
      return today;
    }
    // The range's end date is its last day, so the range runs until the
    // next one starts: an interval ending on it is whole
    return moment.utc(dateScope.endDate).startOf('day').add(1, 'day');
  })();

  const endDate = (() => {
    const e = getEndOf(
      typeof date === 'string' ? DateTime.fromISO(date).toMillis() : date,
      interval,
      fiscalRanges,
    );
    return moment.utc(e).startOf('day');
  })();

  return dateToCompareTo.isBefore(endDate) && !dateToCompareTo.isSame(endDate);
};

export const removePartialIntervalsDates = (
  data: string[],
  interval?: FleetOps.Interval,
  dateScope?: DateRangeInput,
) => {
  // Last Item
  const isLastItemPartial = getIsLastDatePartial({
    date: data[data.length - 1],
    interval,
    dateScope,
  });

  // First Item
  const isFirstItemPartial = (() => {
    if (
      !interval ||
      interval === 'auto' ||
      !dateScope ||
      !dateScope.startDate
    ) {
      return false;
    }

    const dateRangeStart = dateScope.startDate;
    const firstItemDate = data[0];
    return moment.utc(firstItemDate).isBefore(moment.utc(dateRangeStart));
  })();

  if (isFirstItemPartial && isLastItemPartial) {
    return data.slice(1, data.length - 1);
  } else if (isFirstItemPartial) {
    return data.slice(1);
  } else if (isLastItemPartial) {
    return data.slice(0, data.length - 1);
  } else {
    return data;
  }
};

export const removePartialFirstInterval = (
  data: [number, number][],
  interval?: FleetOps.Interval | FiscalInterval,
  dateScope?: DateRangeInput,
) => {
  if (data.length === 0) {
    return [];
  }

  // First Item
  const isFirstItemPartial = (() => {
    if (
      !interval ||
      interval === 'auto' ||
      !dateScope ||
      !dateScope.startDate
    ) {
      return false;
    }

    const dateRangeStart = dateScope.startDate;
    const firstItemDate = data[0][0];

    return (
      moment.utc(firstItemDate).isBefore(moment.utc(dateRangeStart)) &&
      !moment.utc(firstItemDate).isSame(moment.utc(dateRangeStart))
    );
  })();

  if (isFirstItemPartial) {
    return data.slice(1);
  } else {
    return data;
  }
};

const removePartialIntervals = (
  data: [number, number][],
  interval?: FleetOps.Interval | FiscalInterval,
  dateScope?: DateRangeInput,
  fiscalRanges?: FixedPeriods.AllFiscals,
) => {
  if (data.length === 0) {
    return [];
  }

  const dataWithoutFirstPartial = removePartialFirstInterval(
    data,
    interval,
    dateScope,
  );

  // Last Item
  const lastItem = dataWithoutFirstPartial[dataWithoutFirstPartial.length - 1];
  const isLastItemPartial = getIsLastDatePartial({
    date: lastItem[0],
    dateScope,
    interval,
    fiscalRanges,
  });

  if (isLastItemPartial) {
    return dataWithoutFirstPartial.slice(0, dataWithoutFirstPartial.length - 1);
  } else {
    return dataWithoutFirstPartial;
  }
};

export default removePartialIntervals;
